import { useEffect, useState } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { api, APIError } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { clearInvite, stashInvite } from "@/lib/team"
import { Button, Spinner, Toast } from "@/components/ui"
import { LegalLinks } from "@/components/LegalLinks"
import type { PublicTeamInviteOut, TeamOut } from "@/lib/types"
import { UsersIcon } from "lucide-react"

interface AcceptSearch {
  t?: string
}

// Public-ish: works signed out (shows the invite, offers sign in / sign up
// and resumes here afterwards) and signed in (one "Accept" click).
export const Route = createFileRoute("/team/accept")({
  validateSearch: (s: Record<string, unknown>): AcceptSearch => ({
    t: typeof s.t === "string" ? s.t : undefined,
  }),
  component: TeamAcceptPage,
})

function TeamAcceptPage() {
  const t = useT()
  const nav = useNavigate()
  const { t: token } = Route.useSearch()
  const signedIn = auth.isLoggedIn()
  const me = auth.me()
  const [info, setInfo] = useState<PublicTeamInviteOut | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "invalid">("loading")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) { setState("invalid"); setError(t("team.accept.missing")); return }
    stashInvite(token)
    fetch(`/api/team/invites/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) { clearInvite(); setState("invalid"); setError(t("team.accept.invalid")); return }
        const body = (await r.json()) as PublicTeamInviteOut
        setInfo(body)
        if (body.expired || body.accepted) clearInvite()
        setState("ready")
      })
      .catch(() => { setState("invalid"); setError(t("team.accept.invalid")) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function accept() {
    if (!token) return
    setBusy(true); setError(null)
    try {
      await api<TeamOut>("/api/team/accept", { method: "POST", body: { token } })
      clearInvite()
      await nav({ to: "/team" })
    } catch (e) {
      const err = e as APIError
      // Terminal refusals (wrong account, used, expired, already in a
      // workspace): stop resuming this invite on every page load.
      if (err.status && err.status >= 400 && err.status !== 401) clearInvite()
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const terminal = info?.expired || info?.accepted
  return (
    <div className="relative min-h-screen grid place-items-center overflow-hidden px-4 bg-background">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[20%] top-[15%] h-72 w-72 animate-blob-1 rounded-full bg-primary/20 blur-[100px]" />
        <div className="absolute right-[18%] bottom-[18%] h-64 w-64 animate-blob-2 rounded-full bg-violet/15 blur-[100px]" />
      </div>
      <div className="relative z-10 w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card/90 p-7 shadow-glass backdrop-blur">
        <div className="mb-3 flex flex-col items-center text-center">
          <img src="/lucy-logo.png" alt="Lucy" className="mb-3 h-14 w-14 rounded-full object-cover shadow-[0_4px_18px_rgba(192,140,255,0.5)]" />
          <h1 className="text-xl font-semibold tracking-tight">{t("team.accept.title")}</h1>
        </div>

        {state === "loading" && <div className="flex justify-center py-4"><Spinner /></div>}

        {state === "ready" && info && (
          <div className="space-y-3 text-center">
            <div className="flex items-center justify-center gap-2 text-sm">
              <UsersIcon className="h-4 w-4 text-primary" />
              <span>{t("team.accept.body", { inviter: info.inviter_name, workspace: info.workspace_name })}</span>
            </div>
            {!terminal && (
              <p className="text-xs text-muted-foreground">{t("team.accept.emailHint", { email: info.email_hint })}</p>
            )}
            {info.expired && <Toast variant="warn">{t("team.accept.expired")}</Toast>}
            {info.accepted && !info.expired && <Toast variant="warn">{t("team.accept.used")}</Toast>}
          </div>
        )}

        {error && <Toast variant="destructive">{error}</Toast>}

        {state === "ready" && !terminal && signedIn && (
          <div className="space-y-2">
            {me?.email && <p className="text-center text-[11px] text-muted-foreground">{t("team.accept.signedInAs", { email: me.email })}</p>}
            <Button className="w-full" disabled={busy} onClick={accept}>
              {busy ? <Spinner /> : null} {t("team.accept.accept")}
            </Button>
          </div>
        )}

        {state === "ready" && !terminal && !signedIn && (
          <div className="grid grid-cols-2 gap-2">
            <Link to="/login" className="contents"><Button className="w-full">{t("team.accept.signin")}</Button></Link>
            <Link to="/signup" className="contents"><Button variant="outline" className="w-full">{t("team.accept.signup")}</Button></Link>
          </div>
        )}

        <div className="flex items-center justify-center gap-3 pt-1 text-[11px] text-muted-foreground">
          {signedIn ? (
            <Link to="/chat" className="hover:text-foreground" onClick={() => clearInvite()}>
              {terminal || state === "invalid" ? t("team.accept.goChat") : t("team.accept.skip")}
            </Link>
          ) : (
            <Link to="/login" className="hover:text-foreground" onClick={() => clearInvite()}>{t("team.accept.signin")}</Link>
          )}
          <span aria-hidden>·</span>
          <LegalLinks />
        </div>
      </div>
    </div>
  )
}
